import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Inbox, ChevronRight, ChevronDown, Clock, CheckCircle2, Building2, Truck, ScrollText, Receipt, RefreshCw, PenLine } from "lucide-react";
import { EquipmentTypeBadge } from "@/components/EquipmentTypeBadge";
import { formatRateChip } from "@/lib/rentalPricing";
import { ContractTrustPill } from "@/components/ContractTrustPill";
import { CustomerContractDocsList } from "@/components/CustomerContractDocsList";
import OfflineBanner from "@/pwa/OfflineBanner";
import PwaInstallPrompt from "@/pwa/PwaInstallPrompt";
import { CustomerWelcomeCard } from "@/components/CustomerWelcomeCard";
import CancelSubmissionModal from "@/components/CancelSubmissionModal";  // Phase CANCEL-DRAFT (Part B / FE)
import { XCircle } from "lucide-react";
import {
    resolveSubmissionStatus,
    resolveContractStatus,
} from "@/lib/statusResolvers";  // Phase STATE-DRIFT-SWEEP

/**
 * Phase N.7 — inline CTA rendered on the "My Contracts" card when a
 * contract is awaiting the customer's signature but the signing token
 * is either missing or expired. Fires the customer-authed resend
 * endpoint and refreshes the contracts list on success.
 */
function RequestNewSigningLinkButton({ contract, expired, onSent }) {
    const [busy, setBusy] = React.useState(false);
    const handle = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/customer/contracts/${contract.id}/resend-signing-link`,
            );
            toast.success(data?.message || "New signing link sent.");
            onSent?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "rate_limited") {
                const mins = Math.max(1, Math.round((detail.retry_after || 3600) / 60));
                toast.error(`Too many resends recently. Try again in ~${mins} minute${mins === 1 ? "" : "s"}.`);
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="pt-1 space-y-1">
            {expired && (
                <p
                    className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
                    data-testid={`contract-token-expired-note-${contract.id}`}
                >
                    Your previous signing link has expired.
                </p>
            )}
            <Button
                onClick={handle}
                disabled={busy}
                className="w-full gap-1.5 bg-blue-900 hover:bg-blue-800"
                data-testid={`request-new-signing-link-btn-${contract.id}`}
            >
                {busy ? "Sending…" : (
                    <>
                        <PenLine size={14} /> Request new signing link
                    </>
                )}
            </Button>
        </div>
    );
}



const STATUS_STYLES = {
    draft: "bg-amber-50 text-amber-900 ring-amber-200",
    submitted: "bg-blue-50 text-blue-900 ring-blue-200",
    approved: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    rejected: "bg-red-50 text-red-700 ring-red-200",
    // Task 24 — new terminal/limbo states after contract cancellation.
    awaiting_reassignment: "bg-amber-50 text-amber-900 ring-amber-200",
    cancelled_by_admin: "bg-red-50 text-red-800 ring-red-200",
    // Phase CANCEL-DRAFT — soft-deleted draft (customer or admin cancel).
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
    equipment_assigned: "bg-emerald-50 text-emerald-900 ring-emerald-200",
};

// Task 24-A3 — human-readable label per submission state for the
// customer's "My submissions" cards (avoid raw underscored keys).
const SUBMISSION_STATUS_LABEL = {
    draft: "draft",
    submitted: "submitted",
    approved: "approved",
    rejected: "rejected",
    equipment_assigned: "equipment assigned",
    awaiting_reassignment: "awaiting reassignment",
    cancelled_by_admin: "cancelled",
    cancelled: "cancelled",
};

const RENTAL_STATUS_LABEL = {
    pending_contract: "Pending contract",
    contract_signed: "Contract signed",
    coi_pending: "Insurance needed",
    coi_received: "COI under review",
    coi_approved: "COI approved",
    active: "Active",
    ended: "Ended",
    terminated: "Terminated",
    scheduled: "Scheduled",
    imported: "Imported",
};

const RENTAL_STATUS_COLOR = {
    pending_contract: "bg-slate-100 text-slate-700 ring-slate-200",
    contract_signed: "bg-blue-50 text-blue-900 ring-blue-200",
    coi_pending: "bg-amber-50 text-amber-900 ring-amber-200",
    coi_received: "bg-violet-50 text-violet-800 ring-violet-200",
    coi_approved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    active: "bg-emerald-100 text-emerald-900 ring-emerald-300",
    ended: "bg-slate-200 text-slate-700 ring-slate-300",
    terminated: "bg-red-50 text-red-700 ring-red-200",
    scheduled: "bg-blue-50 text-blue-800 ring-blue-200",
    imported: "bg-violet-50 text-violet-800 ring-violet-200",
};

const EQUIPMENT_LABELS = {
    reefer: "Reefer", dry_van: "Dry Van", conestoga: "Conestoga",
    flatbed: "Flatbed", step_deck: "Step Deck", other: "Other",
};

function fmtDate(s) {
    try {
        return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return s;
    }
}

function fmtDay(s) { if (!s) return "—"; return s.slice(0, 10); }

/**
 * Phase CUSTOMER-DASH-COLLAPSE (2026-07-10) — Collapsible dashboard
 * section. Header shows title + icon + count badge (always visible,
 * even when collapsed), plus optional right-side extras (e.g. the
 * "Show cancelled" toggle on submissions). Chevron on the far right.
 *
 * Expand/collapse state is persisted to localStorage under
 * ``storageKey``. Default state = collapsed on first visit; once the
 * customer expands a section, it stays expanded on future visits.
 */
function CollapsibleSection({
    title, icon: Icon, count, storageKey, headerExtras, children, testId,
}) {
    const [expanded, setExpanded] = React.useState(() => {
        try {
            const v = window.localStorage.getItem(storageKey);
            return v === "1";
        } catch { return false; }
    });
    const toggle = () => {
        setExpanded((prev) => {
            const next = !prev;
            try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* noop */ }
            return next;
        });
    };
    return (
        <section className="mt-10" data-testid={testId}>
            <div className="mb-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                        type="button"
                        onClick={toggle}
                        aria-expanded={expanded}
                        data-testid={`${testId}-toggle`}
                        className="group flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
                    >
                        {Icon && <Icon size={18} className="text-slate-500" />}
                        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
                            {title}
                        </h2>
                        {typeof count === "number" && (
                            <span
                                className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-xs font-medium px-2 py-0.5"
                                data-testid={`${testId}-count`}
                            >
                                {count}
                            </span>
                        )}
                        <ChevronDown
                            size={16}
                            className={`text-slate-500 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                            aria-hidden="true"
                        />
                    </button>
                    {headerExtras && <div className="flex items-center gap-2">{headerExtras}</div>}
                </div>
            </div>
            {expanded && <div data-testid={`${testId}-body`}>{children}</div>}
        </section>
    );
}

export default function CustomerDashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [submissions, setSubmissions] = useState([]);
    const [rentals, setRentals] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    // Phase H.8 — surface unclaimed/in-progress invites so customers who
    // dead-ended (or who previously hit the redirect bug) can recover from
    // the dashboard instead of having to dig the invite email back up.
    const [pendingInvites, setPendingInvites] = useState([]);
    // Task 28-B — restart-application dialog state.
    const [restartTarget, setRestartTarget] = useState(null); // submission obj or null
    const [restartBusy, setRestartBusy] = useState(false);
    // Phase CANCEL-DRAFT (Part B / FE) — dashboard toggle + cancel modal state.
    const [showCancelled, setShowCancelled] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);  // submission obj or null
    // Phase N.19 — toggles only render when cancelled items exist.
    const [cancelledSubCount, setCancelledSubCount] = useState(0);
    const [showCancelledContracts, setShowCancelledContracts] = useState(false);

    const refreshCancelledSubCount = useCallback(async () => {
        try {
            const { data } = await api.get("/submissions/mine", { params: { include_cancelled: 1 } });
            setCancelledSubCount((data || []).filter((s) => s.status === "cancelled").length);
        } catch {
            /* non-fatal: toggle simply stays hidden */
        }
    }, []);

    const reloadSubmissions = useCallback(async (include_cancelled) => {
        try {
            const { data } = await api.get(
                "/submissions/mine",
                { params: include_cancelled ? { include_cancelled: 1 } : {} },
            );
            setSubmissions(data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
        refreshCancelledSubCount();
    }, [refreshCancelledSubCount]);

    // Phase N.7 — expose a reload for the contracts list so the
    // "Request new signing link" flow can refresh the CTA state after
    // the resend succeeds (new expires_at is visible).
    const reloadContracts = useCallback(async () => {
        try {
            const { data } = await api.get("/customer/contracts");
            setContracts(data?.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [subRes, profRes, rentRes, ctrRes, invRes] = await Promise.allSettled([
                    api.get("/submissions/mine"),
                    api.get("/customer/profile"),
                    api.get("/customer/rentals"),
                    api.get("/customer/contracts"),
                    api.get("/customer/pending-invites"),
                ]);
                if (subRes.status === "fulfilled") setSubmissions(subRes.value.data);
                else toast.error(formatApiError(subRes.reason));
                if (profRes.status === "fulfilled") setProfile(profRes.value.data);
                if (rentRes.status === "fulfilled") setRentals(rentRes.value.data?.items || []);
                if (ctrRes.status === "fulfilled") setContracts(ctrRes.value.data?.items || []);
                if (invRes.status === "fulfilled") setPendingInvites(invRes.value.data?.items || []);
            } finally {
                setLoading(false);
            }
        })();
        refreshCancelledSubCount();
    }, [refreshCancelledSubCount]);

    const cancelledContractCount = contracts.filter((c) => c.status === "cancelled").length;
    const visibleContracts = showCancelledContracts ? contracts : contracts.filter((c) => c.status !== "cancelled");

    // Task 28-B — restart a cancelled application. POSTs to the
    // customer endpoint which mints a fresh invite using the same form
    // spec and redirects the customer straight into the new draft.
    const handleRestartConfirm = async () => {
        if (!restartTarget) return;
        setRestartBusy(true);
        try {
            const { data } = await api.post(
                `/customer/submissions/${restartTarget.id}/restart`,
            );
            toast.success("Fresh application ready — picking up where the last one left off.");
            setRestartTarget(null);
            // The invite link is the magic-link URL — taking the customer
            // straight there means they don't have to wait for the email.
            if (data?.invite_link) {
                try {
                    const u = new URL(data.invite_link);
                    navigate(u.pathname + u.search);
                } catch {
                    window.location.href = data.invite_link;
                }
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRestartBusy(false);
        }
    };

    return (
        <DashboardShell>
            <OfflineBanner />
            <div className="fade-up">
                <PwaInstallPrompt />
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    Customer portal
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="customer-dashboard-welcome">
                    Welcome, {profile?.company_name || user?.full_name || "there"}
                </h1>
                <p className="mt-3 max-w-2xl text-base text-slate-600 leading-relaxed">
                    {pendingInvites.length > 0
                        ? "Submit your rental application materials in one place. You have an open invitation waiting — pick up where you left off below."
                        : "Submit your rental application materials in one place. Need help? Contact your account manager."}
                </p>
            </div>

            {/* Phase POLISH-1 #3 — Welcome card for first-redemption customers. */}
            <CustomerWelcomeCard />

            {/* Phase H.8 — recovery card. Only shown when at least one
                pending/opened invite is matched to this customer's email and
                there's no submission of theirs already linked to it. */}
            {!loading && pendingInvites.length > 0 && (
                <Card
                    className="mt-8 border-blue-200 bg-gradient-to-br from-blue-50 to-white"
                    data-testid="customer-resume-application-card">
                    <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">
                                Continue your application
                            </p>
                            <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 mt-1">
                                An admin sent you an invitation
                            </h3>
                            <p className="mt-1.5 text-sm text-slate-600 max-w-xl">
                                Pick up where you left off — it only takes a few minutes to finish setting up your account.
                            </p>
                            {pendingInvites.length > 1 && (
                                <p className="mt-2 text-xs text-slate-500" data-testid="customer-resume-multiple-note">
                                    You have {pendingInvites.length} pending invitations. We'll open the most recent one first.
                                </p>
                            )}
                            {pendingInvites[0]?.sent_by_admin_email && (
                                <p className="mt-1 text-xs text-slate-500">
                                    Sent by {pendingInvites[0].sent_by_admin_email}
                                </p>
                            )}
                        </div>
                        <Button
                            asChild
                            className="bg-blue-900 hover:bg-blue-800 h-11 px-5 shrink-0"
                            data-testid="customer-resume-application-button">
                            <Link to={`/invite/${pendingInvites[0].token}`}>
                                Resume application
                                <ChevronRight size={16} className="ml-1" />
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            )}

            {profile && (
                <Card className="mt-8 bg-blue-50/50 border-blue-100" data-testid="customer-profile-card">
                    <CardContent className="p-5 flex items-start gap-4">
                        <div className="h-10 w-10 rounded-md bg-blue-900 text-white flex items-center justify-center shrink-0">
                            <Building2 size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Your company profile</p>
                            <h3 className="font-display text-lg font-semibold text-slate-900 truncate">{profile.company_name || "—"}</h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {profile.last_application_at ? `Last applied ${fmtDate(profile.last_application_at)}` : "No applications yet"}
                            </p>
                        </div>
                        <Button asChild variant="outline" size="sm" className="gap-1.5" data-testid="customer-profile-link">
                            <Link to="/customer/profile">View / edit profile <ChevronRight size={14} /></Link>
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Phase J.1 Pass 3 — invoices entry point */}
            <Card className="mt-6 hover:border-slate-300 transition-colors" data-testid="customer-invoices-link-card">
                <CardContent className="p-5 flex items-center gap-4">
                    <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-900 flex items-center justify-center shrink-0">
                        <Receipt size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Billing</p>
                        <h3 className="font-display text-lg font-semibold text-slate-900">My invoices</h3>
                        <p className="text-xs text-slate-500 mt-0.5">View, download, and (soon) pay invoices online.</p>
                    </div>
                    <Button asChild variant="outline" size="sm" className="gap-1.5" data-testid="customer-invoices-link">
                        <Link to="/customer/invoices">Open invoices <ChevronRight size={14} /></Link>
                    </Button>
                </CardContent>
            </Card>

            <CollapsibleSection
                title="My rentals"
                icon={Truck}
                count={loading ? undefined : rentals.length}
                storageKey="customer.dashboard.myRentals.expanded"
                testId="customer-rentals-section"
            >
                {loading && <p className="text-sm text-slate-500" data-testid="customer-rentals-loading">Loading…</p>}
                {!loading && rentals.length === 0 && (
                    <Card className="border-dashed border-2 border-slate-200 bg-slate-50/60" data-testid="customer-rentals-empty">
                        <CardContent className="py-10 px-6 sm:px-10 text-center">
                            <div className="mx-auto h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                                <Truck size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">No active rentals</h3>
                            <p className="mt-2 max-w-sm mx-auto text-sm text-slate-600">
                                Once a contract is signed, your rentals and their status will appear here.
                            </p>
                        </CardContent>
                    </Card>
                )}
                {!loading && rentals.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customer-rentals-list">
                        {rentals.map((r) => (
                            <Card key={r.id} data-testid={`customer-rental-card-${r.id}`} className="hover:border-slate-300 transition-colors">
                                <CardContent className="p-5 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${RENTAL_STATUS_COLOR[r.status] || "bg-slate-100 text-slate-700"}`}
                                                data-testid={`customer-rental-status-${r.id}`}>
                                                {RENTAL_STATUS_LABEL[r.status] || r.status}
                                            </span>
                                            {r.imported && (
                                                <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-violet-50 text-violet-800 ring-1 ring-violet-200"
                                                    data-testid={`customer-rental-imported-${r.id}`}>Imported</span>
                                            )}
                                        </div>
                                        <span className="text-xs text-slate-500">{fmtDay(r.start_date)} → {fmtDay(r.end_date) || "open"}</span>
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-slate-900">
                                        {EQUIPMENT_LABELS[r.equipment_type] || r.equipment_type || "Rental"}
                                        {r.unit_number && <span className="text-slate-500 font-normal"> · #{r.unit_number}</span>}
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        {[r.year, r.make, r.model].filter(Boolean).join(" ") || "Equipment details TBD"}
                                    </p>
                                    {formatRateChip(r) && (
                                        <p className="text-xs font-semibold text-slate-800" data-testid={`customer-rental-rate-${r.id}`}>
                                            {formatRateChip(r)}
                                        </p>
                                    )}
                                    {r.status === "coi_pending" && (
                                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                                            We've contacted your insurance agent. They'll send the COI directly to us.
                                        </p>
                                    )}
                                    {/* Phase DP-R1 — ended state line + final invoice deep-link. */}
                                    {r.status === "ended" && (
                                        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 mt-2"
                                            data-testid={`customer-rental-ended-${r.id}`}>
                                            This rental ended on <strong>{fmtDay(r.end_date) || "—"}</strong>.
                                            {r.final_invoice_id && (
                                                <>
                                                    {" "}
                                                    <Link to={`/customer/invoices/${r.final_invoice_id}`}
                                                        className="text-blue-900 hover:underline"
                                                        data-testid={`customer-rental-ended-final-invoice-${r.id}`}>
                                                        View final invoice
                                                    </Link>.
                                                </>
                                            )}
                                            {/* Phase DP-R2 — damages invoice deep-link when present. */}
                                            {r.damages_invoice_id && (
                                                <>
                                                    {" "}
                                                    <Link to={`/customer/invoices/${r.damages_invoice_id}`}
                                                        className="text-blue-900 hover:underline"
                                                        data-testid={`customer-rental-ended-damages-invoice-${r.id}`}>
                                                        View damages invoice
                                                    </Link>.
                                                </>
                                            )}
                                            {/* Phase DP-R3 — deposit summary block. */}
                                            {r.deposit && r.deposit.status !== "not_collected" && (
                                                <div className="mt-1 text-slate-700"
                                                    data-testid={`customer-deposit-section-${r.id}`}>
                                                    Security deposit: <strong>${((r.deposit.amount_cents || 0) / 100).toFixed(2)}</strong> collected
                                                    {(r.deposit.applied_amount_cents || 0) > 0 && (
                                                        <>, <strong>${((r.deposit.applied_amount_cents || 0) / 100).toFixed(2)}</strong> applied</>
                                                    )}
                                                    {(r.deposit.refund_amount_cents || 0) > 0 && r.deposit.refund_action && (
                                                        <>, <strong>${((r.deposit.refund_amount_cents || 0) / 100).toFixed(2)}</strong> refunded
                                                          {r.deposit.refund_action === "credit_memo" && " (credit memo)"}
                                                          {r.deposit.refund_action === "stripe_refund" && " (Stripe — in progress)"}
                                                        </>
                                                    )}.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </CollapsibleSection>

            <CollapsibleSection
                title="My contracts"
                icon={ScrollText}
                count={loading ? undefined : visibleContracts.length}
                storageKey="customer.dashboard.myContracts.expanded"
                testId="customer-contracts-section"
                headerExtras={cancelledContractCount > 0 ? (
                    /* Phase N.19 — mirrors the submissions toggle. */
                    <label
                        data-testid="customer-contracts-show-cancelled-toggle"
                        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${
                            showCancelledContracts
                                ? "border-slate-300 bg-slate-50 text-slate-900"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={showCancelledContracts}
                            onChange={(e) => setShowCancelledContracts(e.target.checked)}
                            data-testid="customer-contracts-show-cancelled-input"
                            className="h-3.5 w-3.5"
                        />
                        Show cancelled
                    </label>
                ) : null}
            >
                {loading && <p className="text-sm text-slate-500" data-testid="customer-contracts-loading">Loading…</p>}

                {!loading && visibleContracts.length === 0 && (
                    <Card className="border-dashed border-2 border-slate-200 bg-slate-50/60" data-testid="customer-contracts-empty">
                        <CardContent className="py-10 px-6 sm:px-10 text-center">
                            <div className="mx-auto h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                                <ScrollText size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">No contracts yet</h3>
                            <p className="mt-2 max-w-sm mx-auto text-sm text-slate-600">
                                When your account manager sends you an agreement, it will appear here with a clear trust badge once both parties have signed.
                            </p>
                        </CardContent>
                    </Card>
                )}

                {!loading && visibleContracts.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customer-contracts-list">
                        {visibleContracts.map((c) => {
                            // Phase STATE-DRIFT-SWEEP — single resolver
                            // owns the pill label + tone. Prior code had
                            // an inline if/else that fell through to
                            // ``c.status.replace(/_/g, " ")`` on unmapped
                            // enums (e.g. ``partially_signed``, ``draft``),
                            // leaking raw values to the customer.
                            const cs = resolveContractStatus(c);
                            const fully = cs.isFullySigned;
                            const cancelled = cs.isCancelled;
                            const cancellation = c.cancellation;
                            return (
                                <Card key={c.id} data-testid={`customer-contract-card-${c.id}`} className="hover:border-slate-300 transition-colors">
                                    <CardContent className="p-5 space-y-2">
                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span
                                                    className={
                                                        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " + cs.tone
                                                    }
                                                    data-testid={`customer-contract-status-${c.id}`}>
                                                    {cs.label}
                                                </span>
                                                {fully && <ContractTrustPill contract={c} />}
                                            </div>
                                            <span className="text-xs text-slate-500">
                                                {fmtDate(c.admin_signed_at || c.sent_at || c.created_at)}
                                            </span>
                                        </div>
                                        <h3 className="font-display text-lg font-semibold text-slate-900">
                                            {c.title || "Contract"}
                                        </h3>
                                        {fully && c.customer_signer_name && (
                                            <p className="text-xs text-slate-500" data-testid={`customer-contract-signers-${c.id}`}>
                                                Signed by {c.customer_signer_name}
                                                {c.admin_signer_name ? ` and ${c.admin_signer_name}` : ""}
                                            </p>
                                        )}
                                        {/* Task 24-A2 — surface cancellation context only when the admin
                                            ticked "Show this reason on the customer's contract view". */}
                                        {cancelled && cancellation?.reason && (
                                            <div
                                                className="mt-2 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-xs text-red-900"
                                                data-testid={`customer-contract-cancellation-${c.id}`}
                                            >
                                                <p>
                                                    <span className="font-semibold">Reason:</span>{" "}
                                                    <span data-testid={`customer-contract-cancellation-reason-${c.id}`}>
                                                        “{cancellation.reason}”
                                                    </span>
                                                </p>
                                                {cancellation.cancelled_by_email && (
                                                    <p className="mt-0.5 text-red-800">
                                                        Cancelled by {cancellation.cancelled_by_email}
                                                        {cancellation.cancelled_at ? ` on ${fmtDate(cancellation.cancelled_at)}` : ""}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        {fully && <CustomerContractDocsList contract={c} />}
                                        {/* Phase N.6 — Review & Sign CTA
                                            for customers looking at a
                                            contract that's specifically
                                            awaiting THEIR signature.
                                            Opens the token-scoped public
                                            sign flow. The token is only
                                            surfaced by the API on
                                            ``sent_for_signature`` status
                                            so this branch matches
                                            exactly one state. */}
                                        {cs.awaitingCustomerSignature && (() => {
                                            // Phase N.7 — swap the CTA
                                            // when the token is expired
                                            // or missing. Same visual
                                            // weight (primary blue
                                            // full-width) either way.
                                            const isExpired = c.signing_token_expires_at
                                                && new Date(c.signing_token_expires_at) < new Date();
                                            if (c.signing_token && !isExpired) {
                                                return (
                                                    <div className="pt-1">
                                                        <Button
                                                            asChild
                                                            className="w-full gap-1.5 bg-blue-900 hover:bg-blue-800"
                                                            data-testid={`contract-sign-cta-${c.id}`}
                                                        >
                                                            <Link to={`/sign/contract/${c.signing_token}`}>
                                                                <PenLine size={14} /> Review & Sign
                                                            </Link>
                                                        </Button>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <RequestNewSigningLinkButton
                                                    contract={c}
                                                    expired={isExpired}
                                                    onSent={reloadContracts}
                                                />
                                            );
                                        })()}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </CollapsibleSection>

            <CollapsibleSection
                title="My submissions"
                icon={FileText}
                count={loading ? undefined : submissions.length}
                storageKey="customer.dashboard.mySubmissions.expanded"
                testId="customer-submissions-section"
                headerExtras={(cancelledSubCount > 0 || showCancelled) ? (
                    /* Phase CANCEL-DRAFT (Part B / FE) — dashboard toggle; N.19: only when cancelled exist. */
                    <label
                        data-testid="customer-show-cancelled-toggle"
                        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${
                            showCancelled
                                ? "border-slate-300 bg-slate-50 text-slate-900"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={showCancelled}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setShowCancelled(next);
                                reloadSubmissions(next);
                            }}
                            data-testid="customer-show-cancelled-input"
                            className="h-3.5 w-3.5"
                        />
                        Show cancelled
                    </label>
                ) : null}
            >
                {loading && (
                    <p className="text-sm text-slate-500" data-testid="customer-loading">Loading…</p>
                )}

                {!loading && submissions.length === 0 && (
                    <Card data-testid="customer-empty-submissions" className="border-dashed border-2 border-slate-200 bg-slate-50/60">
                        <CardContent className="py-16 px-6 sm:px-10 text-center">
                            <div className="mx-auto h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                                <Inbox size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
                                No submissions yet
                            </h3>
                            <p className="mt-2 max-w-sm mx-auto text-sm text-slate-600">
                                Open the invite link from your account manager to start your application. Your drafts and submitted applications will appear here.
                            </p>
                        </CardContent>
                    </Card>
                )}

                {!loading && submissions.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customer-submissions-list">
                        {submissions.map((s) => {
                            // Phase STATE-DRIFT-SWEEP — single resolver
                            // owns the pill, one-line summary and the
                            // primary-action button. Prior code had 3
                            // separate inline chains (CUST-UX-FIX bug
                            // root). See STATE_DRIFT_AUDIT.md rows 6+7.
                            const ss = resolveSubmissionStatus(s);
                            const isAwaitingReassignment = s.status === "awaiting_reassignment";
                            const isCancelledByAdmin = s.status === "cancelled_by_admin";
                            const isCancelledDraft = s.status === "cancelled";
                            const isTerminal = ss.isTerminal;
                            const isCustomerSideComplete = ss.isCustomerSideComplete;
                            const reasonVisible = s.cancellation_reason_visible_to_customer !== false;
                            const cancellationReason = reasonVisible ? (s.cancellation_reason || "").trim() : "";
                            return (
                            <Card key={s.id} data-testid={`submission-card-${s.id}`}
                                className={`hover:border-slate-300 transition-colors ${isCancelledDraft ? "opacity-70 bg-slate-50/70" : ""}`}>
                                <CardContent className={`p-5 ${isCancelledDraft ? "[&_h3]:line-through" : ""}`}>
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <span
                                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${ss.tone}`}
                                                data-testid={`submission-status-${s.id}`}
                                            >
                                                {(ss.code === "submitted" || isCustomerSideComplete) ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                                                {ss.label}
                                            </span>
                                        </div>
                                        <span className="text-xs text-slate-500">Updated {fmtDate(s.updated_at)}</span>
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-slate-900">
                                        Rental application
                                    </h3>
                                    {s.data?.equipment_type && (
                                        <span className="inline-block mt-1" data-testid={`submission-equipment-${s.id}`}>
                                            <EquipmentTypeBadge data={s.data} />
                                        </span>
                                    )}
                                    {!isTerminal && (
                                        <p className="text-xs text-slate-500 mt-1">
                                            {ss.code === "submitted"
                                                ? `Submitted ${fmtDate(s.submitted_at)}`
                                                : ss.summary}
                                        </p>
                                    )}
                                    {/* Task 24-A3 — context-aware copy for terminal states. */}
                                    {isAwaitingReassignment && (
                                        <div
                                            className="mt-3 rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900"
                                            data-testid={`submission-awaiting-reassignment-${s.id}`}
                                        >
                                            <p className="font-semibold">Equipment is being reassigned by your fleet manager.</p>
                                            <p className="mt-1">We&apos;ll email you when the new contract is ready to sign.</p>
                                            {cancellationReason && (
                                                <p className="mt-1 text-amber-800">Note: “{cancellationReason}”</p>
                                            )}
                                        </div>
                                    )}
                                    {isCancelledByAdmin && (
                                        <div
                                            className="mt-3 rounded-md border border-red-200 bg-red-50/70 px-3 py-2 text-xs text-red-900"
                                            data-testid={`submission-cancelled-by-admin-${s.id}`}
                                        >
                                            <p className="font-semibold">This submission was cancelled by your fleet manager.</p>
                                            {cancellationReason && (
                                                <p className="mt-1">Reason: “{cancellationReason}”</p>
                                            )}
                                            {/* Task 28-B — one-click recovery. We avoid
                                                showing this if the submission was already
                                                resent (resent_as_invite_id stamped). */}
                                            {!s.resent_as_invite_id ? (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    onClick={() => setRestartTarget(s)}
                                                    data-testid={`restart-application-${s.id}`}
                                                    className="mt-3 gap-1.5 bg-emerald-700 text-white hover:bg-emerald-600"
                                                >
                                                    <RefreshCw size={14} /> Restart application
                                                </Button>
                                            ) : (
                                                <p className="mt-2 text-red-800/80">
                                                    A fresh application link was already sent — check your email.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {!isTerminal && (
                                        <div className="mt-4 flex items-center justify-between">
                                            <Button asChild variant="outline" size="sm" className="gap-1.5">
                                                <Link to={`/customer/submissions/${s.id}`} data-testid={`open-submission-${s.id}`}>
                                                    {ss.primaryAction.label} <ChevronRight size={14} />
                                                </Link>
                                            </Button>
                                            {/* Phase CANCEL-DRAFT — self-cancel link on draft cards only. */}
                                            {ss.cancelable && (
                                                <button
                                                    type="button"
                                                    onClick={() => setCancelTarget(s)}
                                                    data-testid={`cancel-application-${s.id}`}
                                                    className="text-xs text-slate-500 hover:text-red-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
                                                >
                                                    <XCircle size={12} /> Cancel application
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {/* Phase CANCEL-DRAFT — cancelled-draft context block. */}
                                    {isCancelledDraft && (
                                        <div
                                            className="mt-3 rounded-md border border-slate-200 bg-slate-100/70 px-3 py-2 text-xs text-slate-600"
                                            data-testid={`submission-cancelled-draft-${s.id}`}
                                        >
                                            <p className="font-semibold text-slate-700">
                                                Cancelled {s.cancelled_at ? `on ${fmtDate(s.cancelled_at)}` : ""}
                                            </p>
                                            {cancellationReason && (
                                                <p className="mt-0.5">Reason: {cancellationReason}</p>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            );
                        })}
                    </div>
                )}
            </CollapsibleSection>

            {/* Phase CANCEL-DRAFT — tiered "Cancel this application?" modal. */}
            <CancelSubmissionModal
                open={!!cancelTarget}
                onOpenChange={(o) => !o && setCancelTarget(null)}
                target={cancelTarget ? { entityType: "submission", id: cancelTarget.id } : null}
                surface="customer"
                fieldsFilledCount={cancelTarget ? Object.values(cancelTarget?.data || {}).filter((v) => v != null && v !== "").length : 0}
                filesCount={cancelTarget ? (cancelTarget?.uploads?.length || 0) : 0}
                companyInfoPresent={!!(cancelTarget?.data?.company_name || cancelTarget?.data?.company_legal_name)}
                guarantorInfoPresent={!!(cancelTarget?.data?.guarantor_name || cancelTarget?.data?.guarantor_ssn_last4)}
                onCancelled={async () => { await reloadSubmissions(showCancelled); }}
            />

            {/* Task 28-B — confirm modal before restarting a cancelled
                application. Confirms intent, then POSTs and redirects
                the customer straight into the freshly-minted invite. */}
            <Dialog
                open={!!restartTarget}
                onOpenChange={(o) => !o && !restartBusy && setRestartTarget(null)}
            >
                <DialogContent data-testid="restart-application-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Restart your application?</DialogTitle>
                        <DialogDescription>
                            Start a fresh application using the same details? You&apos;ll
                            receive a new application link right away and can fill it
                            out without waiting for an email.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            disabled={restartBusy}
                            onClick={() => setRestartTarget(null)}
                            data-testid="restart-application-cancel"
                        >
                            Not now
                        </Button>
                        <Button
                            onClick={handleRestartConfirm}
                            disabled={restartBusy}
                            data-testid="restart-application-confirm"
                            className="bg-emerald-700 text-white hover:bg-emerald-600 gap-1.5"
                        >
                            {restartBusy ? "Working…" : (
                                <>
                                    <RefreshCw size={14} /> Restart application
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardShell>
    );
}
